import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { type KeyboardEvent, useState } from 'react';
import { useIntl } from 'react-intl';

import { useRenameAvMutation } from '../api/useRenameAvMutation';
import { hasInvalidDisplayNameCharacters } from '../utils/displayNameValidation';

export interface VariableNameInlineEditProps {
  analyticalVariableId: string;
  /** Committed display name shown when not editing. */
  name: string;
  /** When false, the name is read-only (no edit affordance) — non-creator
   * or flag off. */
  isEditable: boolean;
  /** Stable testid prefix, matching AvContentCard's `${dataTestId}-*` convention. */
  dataTestId: string;
  /** Project this variable is being viewed under, when known — threaded to
   * useRenameAvMutation so it can target the correct libraryDetail cache
   * slot (projectId participates in that key) and optimistically patch the
   * project artifact list. */
  projectId?: string;
}

/**
 * Inline-editable variable name for the AV detail page header (OR-6090,
 * AC1c). Click the name to edit; Enter saves, Esc cancels — mirrors the
 * concept-group name inline-edit UX used elsewhere in the app. Renders a
 * plain, non-interactive title when ``isEditable`` is false so read-only
 * viewers see the same header they always have.
 */
export const VariableNameInlineEdit = ({
  analyticalVariableId,
  name,
  isEditable,
  dataTestId,
  projectId,
}: VariableNameInlineEditProps) => {
  const { formatMessage } = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);
  const [hasInvalidCharacters, setHasInvalidCharacters] = useState(false);
  const { mutateAsync, isPending, error, reset } = useRenameAvMutation();

  const onStartEdit = () => {
    setEditedName(name);
    setHasInvalidCharacters(false);
    reset();
    setIsEditing(true);
  };

  const onCancel = () => {
    reset();
    setIsEditing(false);
  };

  const onChangeEditedName = (value: string) => {
    setEditedName(value);
    if (hasInvalidCharacters) setHasInvalidCharacters(false);
  };

  const onSave = async () => {
    const next = editedName.trim();
    if (!next || next === name) {
      setIsEditing(false);
      return;
    }
    // QoL mirror of the backend's hard restriction (see
    // displayNameValidation.ts) — block the mutation instead of letting the
    // server round-trip reject it.
    if (hasInvalidDisplayNameCharacters(next)) {
      setHasInvalidCharacters(true);
      return;
    }
    try {
      await mutateAsync({ analyticalVariableId, dataElement: next, projectId });
      setIsEditing(false);
    } catch {
      // Error surfaces via `error` below; stay open so the user can retry.
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void onSave();
    if (e.key === 'Escape') onCancel();
  };

  if (!isEditable) {
    return (
      <Typography
        variant="h5"
        component="h1"
        data-testid={`${dataTestId}-title`}
        sx={{ fontWeight: 400, minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {name}
      </Typography>
    );
  }

  if (isEditing) {
    return (
      <Box
        data-testid={`${dataTestId}-title-edit`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0 }}
      >
        <TextField
          data-testid={`${dataTestId}-title-input`}
          value={editedName}
          onChange={(e) => onChangeEditedName(e.target.value)}
          onKeyDown={handleKeyDown}
          error={Boolean(error) || hasInvalidCharacters}
          helperText={
            hasInvalidCharacters
              ? formatMessage({
                  id: 'projects.analyticalVariables.renameInline.invalidCharacterErrorMsg',
                })
              : error?.message
          }
          size="small"
          autoFocus
          disabled={isPending}
          // TODO: no VDS token for this value — needs designer input (no sizing/width
          // token category exists yet; mirrors the same untokenized 480 in
          // ConceptGroupNameInlineEdit, the precedent this component follows).
          sx={{ flex: 1, minWidth: 0, maxWidth: 480 }}
        />
        <IconButton
          data-testid={`${dataTestId}-title-save`}
          size="small"
          onClick={() => void onSave()}
          disabled={isPending}
          color="primary"
          aria-label={formatMessage({ id: 'projects.analyticalVariables.renameInline.saveAria' })}
          sx={{ mt: 1 }}
        >
          {isPending ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <CheckIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          data-testid={`${dataTestId}-title-cancel`}
          size="small"
          onClick={onCancel}
          disabled={isPending}
          aria-label={formatMessage({ id: 'projects.analyticalVariables.renameInline.cancelAria' })}
          sx={{ mt: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      data-testid={`${dataTestId}-title-view`}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
    >
      <Typography
        variant="h5"
        component="h1"
        data-testid={`${dataTestId}-title`}
        onClick={onStartEdit}
        sx={{ fontWeight: 400, minWidth: 0, overflowWrap: 'anywhere', cursor: 'text' }}
      >
        {name}
      </Typography>
      <IconButton
        data-testid={`${dataTestId}-title-edit-button`}
        size="small"
        onClick={onStartEdit}
        aria-label={formatMessage({ id: 'projects.analyticalVariables.renameInline.editAria' })}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
